// Handicap math. Pure functions, no imports outside engine/domain types.

import type { Course, Round, Tee } from '../types';
import { sliceCourse } from './holeSet';
import type { BaseConfig, RoundCtx } from './types';

/** Handicaps are capped at 54.0. The plus side is open-ended in theory; the world's best
 *  amateurs sit near +6, so +10 is a generous bound that still catches typos. */
export const MAX_HANDICAP_INDEX = 54;
export const MIN_HANDICAP_INDEX = -10;

/**
 * Parse a handicap index the way a golfer writes it.
 *
 * Better-than-scratch is written with a leading plus ("+2.4") but stored negative, because
 * the player gives strokes back rather than receiving them. Both "+2.4" and "-2.4" parse to
 * -2.4: nobody typing either one means "receives −2.4 strokes", they mean better than scratch.
 * Returns null for anything that isn't a valid index, including out-of-range values.
 */
export function parseHandicapIndex(text: string): number | null {
  const m = /^([+-])?(\d+(?:\.\d+)?)$/.exec(text.trim());
  if (!m) return null;
  const magnitude = parseFloat(m[2]);
  if (!Number.isFinite(magnitude)) return null;
  const index = m[1] ? -magnitude : magnitude;
  if (index > MAX_HANDICAP_INDEX || index < MIN_HANDICAP_INDEX) return null;
  return index;
}

/** Course strokes: index × slope/113 + (rating − par), rounded to nearest integer. */
export function courseHandicap(
  index: number | null,
  slope: number,
  rating: number,
  par: number
): number {
  if (index === null) return 0;
  return Math.round(index * (slope / 113) + (rating - par));
}

export function teePar(tee: Tee): number {
  return tee.holes.reduce((s, h) => s + h.par, 0);
}

/**
 * Allocate `ch` handicap strokes across holes by stroke index.
 * Positive ch: 1 stroke on each of the `ch` hardest holes (SI ≤ ch), a 2nd stroke where SI ≤ ch − 18, etc.
 * Negative ch (plus handicap): give strokes back starting from the EASIEST hole (SI 18, 17, ...).
 * Returns an array parallel to `strokeIndexes` of strokes received on each hole (can be negative).
 */
export function allocateStrokes(ch: number, strokeIndexes: number[]): number[] {
  const n = strokeIndexes.length;
  const out = new Array<number>(n).fill(0);
  if (ch === 0 || n === 0) return out;
  const abs = Math.abs(ch);
  const base = Math.floor(abs / n);
  const rem = abs % n;
  for (let i = 0; i < n; i++) {
    let extra = 0;
    if (ch > 0) {
      // hardest holes first: SI 1..rem get the remainder stroke
      extra = strokeIndexes[i] <= rem ? 1 : 0;
    } else {
      // easiest holes first: SI n, n-1, ... get the remainder stroke
      extra = strokeIndexes[i] > n - rem ? 1 : 0;
    }
    const v = base + extra;
    out[i] = v === 0 ? 0 : Math.sign(ch) * v;
  }
  return out;
}

/**
 * A player's tee, falling back to the course's first tee when the id is not on the course.
 *
 * Every other tee lookup in the app already falls back this way (engine/holeSet.ts,
 * scoreUtils.ts, the scorecard screens); this one threw, and the throw reached a screen: a
 * hosted round built before its event had a course card points every player at the
 * placeholder `platform-tee`, and the moment the card arrives the local course carries real
 * tees instead — the hole screen then crashed on launch for exactly the phones that had
 * joined first. A stored round must never take a screen down over a tee id; a course with no
 * tees at all is the only thing still worth an exception.
 */
const teeFor = (course: Course, teeId: string): Tee => {
  const tee = course.tees.find((t) => t.id === teeId) ?? course.tees[0];
  if (!tee) throw new Error(`Course ${course.id} has no tees`);
  return tee;
};

/**
 * Strokes received per participant per hole for one game's config.
 * gross → all zeros. net/full → full CH. net/usga-diff → CH minus the group's lowest CH.
 * Each player's strokes are allocated against THEIR OWN tee's stroke indexes.
 */
export function strokesReceivedForGame(
  course: Course,
  round: Round,
  config: BaseConfig
): Record<string, number[]> {
  const out: Record<string, number[]> = {};
  const parts = round.players.filter((p) => config.participantIds.includes(p.playerId));
  const holeCount = parts.length ? teeFor(course, parts[0].teeId).holes.length : 0;
  if (config.scoring === 'gross') {
    for (const p of parts) out[p.playerId] = new Array(holeCount).fill(0);
    return out;
  }
  const lowest = Math.min(...parts.map((p) => p.courseHandicap));
  for (const p of parts) {
    const ch =
      config.handicapMode === 'usga-diff' ? p.courseHandicap - lowest : p.courseHandicap;
    const si = teeFor(course, p.teeId).holes.map((h) => h.strokeIndex);
    out[p.playerId] = allocateStrokes(ch, si);
  }
  return out;
}

/**
 * Build the ctx an engine's compute() receives, for one game's config.
 *
 * The course is sliced to the round's played holes first, so `ctx.course.tees[].holes[i]`
 * lines up with `ctx.round.holes[i]` on a nine exactly as it does on a full 18. Every engine
 * and `parFor` therefore works on nines with no changes of their own.
 */
export function buildCtx(course: Course, round: Round, config: BaseConfig): RoundCtx {
  const played = sliceCourse(course, round.holeSet ?? 'full');
  return { course: played, round, strokesReceived: strokesReceivedForGame(played, round, config) };
}

/** Net score for a player on a hole (0-based holeIdx); null if gross not entered. */
export function netScore(
  ctx: RoundCtx,
  playerId: string,
  holeIdx: number
): number | null {
  const gross = ctx.round.holes[holeIdx]?.scores[playerId]?.gross ?? null;
  if (gross === null) return null;
  return gross - (ctx.strokesReceived[playerId]?.[holeIdx] ?? 0);
}
